import axios from "axios";
import { _axios } from "../../utils/axios";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import { ErrorToast, SuccessToast } from "../../components/toastMessage";
import { BASE_URL } from "../../constants/Constants";
import style from "../createAccount/createAccount.module.css";
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const CreateAccount = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const navigate = useNavigate();
  // Add reCAPTCHA token
  const [token, setToken] = useState('');

  const handleVerify = (token: React.SetStateAction<string>) => {
    setToken(token);
    console.log('recaptcha V3', token);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };
  const namePattern = escapeRegExp(name.trim());
  const emailPattern = escapeRegExp(email.trim());
  const mobilePattern = escapeRegExp(mobile.trim());

  const validatePassword = (pwd: string): void => {
    const strongRegex = new RegExp(
      `^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?!.*${name})(?!.*${email})(?!.*${mobile})(?!.*(.)\\1{2})(?!.*(123|234|345|456|567|678|789|987|876|765|654|543|432|321|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz))(?=.{8,})`
    );

    const mediumRegex = new RegExp(
      `^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?!.*${name})(?!.*${email})(?!.*${mobile})(?!.*(.)\\1{2})(?=.{7,})`
    );

    if (strongRegex.test(pwd)) {
      setPasswordStrength('strong');
    } else if (mediumRegex.test(pwd)) {
      setPasswordStrength('medium');
    } else if (pwd.length > 0) {
      setPasswordStrength('weak');
    } else {
      setPasswordStrength('');
    }
  };

  useEffect(() => {
    validatePassword(password);
  }, [password, name, email, mobile]);

  const onRegister = () => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const isValid = emailRegex.test(email);
    if (name.length === 0) {
      ErrorToast("Please enter a valid Name")
      return
    }
    if (email.length === 0 || !isValid) {
      ErrorToast("Please enter a valid Email Id")
      return
    }
    if (mobile.length === 0 || mobile.length < 10 || mobile.length > 10) {
      ErrorToast("Please enter a valid Mobile Number")
      return
    }
    if (password.length === 0) {
      ErrorToast("Please enter a valid Password")
      return
    }
    if (passwordStrength === 'weak' || passwordStrength === 'medium') {
      ErrorToast("Please enter a password with a minimum of 8 characters and ensure it is not sequential (e.g. 123/abc).")
      return
    }
    _axios.post(`${BASE_URL}/api/users`,
      {
        "name": name,
        "mobile": mobile,
        "email": email,
        "password": password,
        "token": token
      })
      .then(function (response) {
        console.log('response', response);
        if (response?.data?.success === 0) {
          ErrorToast(response?.data?.message)
          return
        }
        SuccessToast('Account Created')
        navigate('/onboarding/login');
      })
      .catch(function (error) {
        if (error?.response?.data?.message) {
          ErrorToast(error?.response?.data?.message)
        }
        console.log('error', error);
      });
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s-]*$/.test(value)) {
      setName(value);
    }
  };

  // const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   setPassword(value);
  //   setPasswordStrength(checkPasswordStrength(value));
  // };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'strong':
        return 'text-success';
      case 'medium':
        return 'text-warning';
      case 'weak':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey="6LdRltoqAAAAAKddCuFxu0SGioWr8tMtYSESl_ol">
      <div>
        <div className={style.loginContainer}>
          <div className={style.loginForm}>
            <div className={style.logo}>
              <img
                src={`${BASE_URL}/api/logo`}
                alt="Logo"
              />
            </div>
            <div className={style.loginText}>Registration</div>

            <br />

            <Input
              title={"Full Name"}
              placeholder={"Full Name"}
              isIcon={true}
              icon={"fa fa-user"}
              type="text"
              value={name}
              onChange={handleNameChange}
            />

            <Input
              title={"Email"}
              placeholder={"Email"}
              isIcon={true}
              icon={"fa fa-envelope"}
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              title={"Mobile Number"}
              placeholder={"Mobile Number"}
              isIcon={true}
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />

            <Input
              title={"Password"}
              placeholder={"Password"}
              isIcon={true}
              icon={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
              type={showPassword ? "text" : "password"}
              onClick={togglePasswordVisibility}
              value={password}
              onChange={handlePasswordChange}
            />

            <div className={style.passwordStrength}>
              <span>Password strength: </span>
              <span className={`${style.strengthIndicator} ${getPasswordStrengthColor()}`}>
                {passwordStrength || '--'}
              </span>
            </div>

            <button
              className={style.sendButton}
              onClick={onRegister}
            >
              Register
            </button>

            <div className={style.createAccount}>
              Already have an account?
              <Link to="/onboarding/login"> Login</Link>
            </div>
            <div className={style.passwordHint}>
              Password must be strong: Minimum 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
              has context menu
            </div>
          </div>
        </div>
      </div>
      <GoogleReCaptcha onVerify={handleVerify} />
    </GoogleReCaptchaProvider>
  )
}

export default CreateAccount